import React from "react";
import ReactCrop, { Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Field, WrappedFieldInputProps, WrappedFieldProps } from "redux-form";

import { FormControl } from "@/mk2/components/forms/FormControl";
import { PhotoEntity } from "@/mk2/schemas";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./PhotoCropField.mscss";

interface PhotoCropInputProps {
    input: WrappedFieldInputProps;
    photo: PhotoEntity;
    aspect?: number; // width / height of the crop selection, defaults to 1 (square)
    // when true, the default selection is written to the form value on mount (so the
    // user does not have to touch the widget to get a valid crop). Off by default to
    // keep the original avatar-crop behaviour (crop only after user interaction).
    autoSelectInitialCrop?: boolean;
}

interface PhotoCropInputState {
    crop: Crop;
}

const PHOTO_PREVIEW_WIDTH = 250;

class PhotoCropInput extends React.Component<PhotoCropInputProps, PhotoCropInputState> {
    private inlineStyles = { width: PHOTO_PREVIEW_WIDTH };

    constructor(props) {
        super(props);

        const { photo, aspect = 1 } = props;

        const w = PHOTO_PREVIEW_WIDTH; // smaller image will be upscaled
        const scale = PHOTO_PREVIEW_WIDTH / photo.width;
        const h = photo.height * scale;
        const minSize = Math.min(100 * scale, photo.height * scale, photo.width * scale);

        // initial selection width is 100 preview pixels (or minSize), but not wider than
        // the image; height is derived from the aspect ratio and clamped to image height
        let selw = Math.min(Math.max(100, minSize), w);
        let selh = selw / aspect;
        if (selh > h) {
            selh = h;
            selw = selh * aspect;
        }

        this.state = {
            crop: {
                unit: "px",
                x: ((w - selw) * 6) / 16, // split remaining space with golden ratio
                y: ((h - selh) * 6) / 16,
                width: selw,
                height: selh,
                aspect,
            },
        };
    }

    public componentDidMount() {
        // seed the form value with the default selection so a valid crop exists even
        // without user interaction. Remounting the field (via `key`, e.g. when a
        // different photo is picked) re-seeds a fresh default for the new photo.
        const { input, autoSelectInitialCrop } = this.props;
        if (autoSelectInitialCrop) {
            const { crop } = this.state;
            input.onChange([crop.x, crop.y, crop.width]);
        }
    }

    public render() {
        const { photo, aspect = 1 } = this.props;

        const scale = PHOTO_PREVIEW_WIDTH / photo.width;
        const minSize = Math.min(100 * scale, photo.height * scale, photo.width * scale);

        return (
            <div className={styles.PhotoCropInput}>
                <ReactCrop
                    src={photoUrl(photo, "s1600x1600")}
                    crop={this.state.crop}
                    onChange={this.onCropChange}
                    onComplete={this.onComplete}
                    imageStyle={this.inlineStyles}
                    minWidth={minSize}
                    minHeight={minSize / aspect}
                />
            </div>
        );
    }

    private onCropChange = (crop: Crop) => {
        this.setState({ crop });
    };

    private onComplete = (crop: Crop) => {
        const { input } = this.props;

        const hasChangedCrop =
            crop.width === 0
                ? !!input.value
                : !input.value ||
                  input.value.length !== 3 ||
                  input.value[0] !== crop.x ||
                  input.value[1] !== crop.y ||
                  input.value[2] !== crop.width;

        if (hasChangedCrop) {
            input.onChange(crop.width === 0 ? null : [crop.x, crop.y, crop.width]);
        }
    };
}

interface PhotoCropControlPublicProps {
    label?: string;
    marginLeft?: boolean;
    bottomBorder?: boolean;
    photo: PhotoEntity;
    aspect?: number;
    autoSelectInitialCrop?: boolean;
}

type PhotoCropControlProps = PhotoCropControlPublicProps & WrappedFieldProps;

const PhotoCropControl: React.StatelessComponent<PhotoCropControlProps> = ({
    input,
    meta,
    label,
    marginLeft,
    bottomBorder, // WrappedFieldProps
    photo,
    aspect,
    autoSelectInitialCrop, // PhotoCropControlPublicProps
}) => (
    <FormControl
        input={input}
        meta={meta}
        label={label}
        marginLeft={marginLeft}
        bottomBorder={bottomBorder}
        minimizedLabel={true /* always minimize, otherwise label overlaps with this field */}
    >
        <PhotoCropInput
            photo={photo}
            input={input}
            aspect={aspect}
            autoSelectInitialCrop={autoSelectInitialCrop}
        />
    </FormControl>
);

type PhotoCropFieldProps = PhotoCropControlPublicProps & {
    // see https://redux-form.com/7.3.0/docs/api/field.md/#props-you-can-pass-to-code-field-code-
    name: string;
};

export const PhotoCropField: React.StatelessComponent<PhotoCropFieldProps> = (props) => (
    <Field {...props} component={PhotoCropControl} />
);
